import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiBarChart2, FiUser } from 'react-icons/fi';
import api from '../../utils/api';
import { PERMISSIONS, buildRoleFeatureMatrix, canFeatureForRole } from '../../utils/permissions';
import { isAbandonedMatchFeedback } from '../../utils/matchFeedbackAbandoned';
import { formatMatchDayPillLabel } from '../../utils/matchFeedbackSchedule';
import { formatDateTime } from '../../utils/feedbackViewFormat';
import {
  averageScoreBadge,
  FEEDBACK_SUMMARY_CARD_OUTER,
  FEEDBACK_TABLE_HEAD_GRADIENT,
  formatScoreBadgeText,
  metricScoreBadge,
  SUMMARY_DETAIL_ITEM,
  SUMMARY_DETAIL_LABEL,
  SUMMARY_DETAIL_VALUE_SMALL,
  SUMMARY_DETAILS_GRID,
} from '../../utils/feedbackViewSharedUi';
import { isDaysMatchType, resolveFeedbackDayNumber as resolveDayNumber } from '../../utils/matchTypeUi';
import {
  getRoleIconComponent,
  TEAM_MEMBER_MODAL_ACCENT,
} from '../../utils/teamMemberDetailModalUi';

const formatScoreOrRemarkForDisplay = (value) => {
  if (value == null || value === '') return '-';
  const u = String(value).trim().toUpperCase();
  if (u === 'AB' || u === 'N/A') return 'NA';
  return value;
};

const formatSingleDecimal = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return value ?? '-';
  return num.toFixed(1);
};

const getDayPillStyle = (isSelected) => {
  if (isSelected) {
    return {
      background: '#2563eb',
      border: '1px solid #2563eb',
      color: 'white',
    };
  }
  return {
    background: '#f8fafc',
    border: '1px solid #d0d5dd',
    color: '#475467',
  };
};

const MatchFeedbackViewPage = () => {
  const { matchId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const appBasePath = location.pathname.startsWith('/employee') ? '/employee' : '/admin';
  const [loading, setLoading] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [selectedDay, setSelectedDay] = useState('');

  useEffect(() => {
    if (!matchId) return;
    const load = async () => {
      setLoading(true);
      try {
        let matrix = {};
        try {
          const permResponse = await api.getPermissions();
          const permissionRows = Array.isArray(permResponse?.data)
            ? permResponse.data
            : Array.isArray(permResponse?.data?.permissions)
            ? permResponse.data.permissions
            : Array.isArray(permResponse?.permissions)
            ? permResponse.permissions
            : [];
          matrix = buildRoleFeatureMatrix(permissionRows);
        } catch {
          matrix = {};
        }
        let user = {};
        try {
          user = JSON.parse(sessionStorage.getItem('user') || '{}');
        } catch {
          user = {};
        }
        const role = user?.role;
        // Match TL read: only "View TL Feedback" (Matches module), not team-event feedback ORs.
        if (!canFeatureForRole(role, matrix, [PERMISSIONS.VIEW_MATCH_TL_FEEDBACK])) {
          toast.error('You do not have permission to view match TL feedback');
          const fallbackFrom = String(location.state?.from || '').trim();
          navigate(fallbackFrom || `${appBasePath}/matches`);
          setLoading(false);
          return;
        }

        const res = await api.getEventFeedbackByMatchId(matchId);
        // API returns { success: true, data: { feedback: [...], ... } }
        // Handle both { data: {...} } and raw object shapes
        let data = null;
        if (res?.success && res?.data) {
          data = res.data;
        } else if (res?.data?.feedback) {
          data = res.data;
        } else if (res?.feedback) {
          data = res;
        } else {
          data = res?.data || res;
        }
        setFeedbackData(data);
      } catch (err) {
        toast.error(err?.status === 404 ? 'No feedback found for this match' : (err.message || 'Failed to load feedback'));
        const fallbackFrom = String(location.state?.from || '').trim();
        navigate(fallbackFrom || `${appBasePath}/matches`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [matchId, navigate, location.state, appBasePath]);

  const rawPayload = feedbackData?.data || feedbackData || null;
  const records = Array.isArray(rawPayload)
    ? rawPayload
    : Array.isArray(rawPayload?.feedbacks)
    ? rawPayload.feedbacks
    : Array.isArray(rawPayload?.data)
    ? rawPayload.data
    : rawPayload
    ? [rawPayload]
    : [];

  const selectedEventTeamId = String(location.state?.eventTeamId || '').trim();
  const validRecords = records.filter((record) => record && typeof record === 'object');
  const scopedRecords = selectedEventTeamId
    ? validRecords.filter(
        (record) =>
          String(record?.eventTeamId?._id || record?.eventTeamId?.id || record?.eventTeamId || '').trim() ===
          selectedEventTeamId
      )
    : validRecords;
  const dayOptions = useMemo(
    () =>
      Array.from(
        new Set(
          scopedRecords.flatMap((record) => {
            const days = [];
            const recordDay = resolveDayNumber(record);
            if (recordDay) days.push(recordDay);
            const feedbackEntries = Array.isArray(record?.feedback) ? record.feedback : [];
            feedbackEntries.forEach((entry) => {
              const entryDay = resolveDayNumber(entry);
              if (entryDay) days.push(entryDay);
            });
            return days;
          })
        )
      ).sort((a, b) => a - b),
    [scopedRecords]
  );

  const baseRecord = scopedRecords[0] || validRecords[0] || {};
  const match = baseRecord.matchId || {};
  const matchTypeFromState = location.state?.matchType;
  const isDaysMatch = isDaysMatchType(
    matchTypeFromState || baseRecord?.matchType || match?.matchType
  );
  const showDayTabs = isDaysMatch && dayOptions.length > 0;

  useEffect(() => {
    if (!showDayTabs) return;
    const n = Number(selectedDay);
    if (!selectedDay || !dayOptions.includes(n)) {
      setSelectedDay(String(dayOptions[0]));
    }
  }, [dayOptions, showDayTabs, selectedDay]);

  const filteredRecords = scopedRecords;
  const resolvedDayNumber =
    showDayTabs && dayOptions.length > 0
      ? dayOptions.includes(Number(selectedDay))
        ? Number(selectedDay)
        : dayOptions[0]
      : null;

  const team = baseRecord.eventTeamId || match.eventTeam || {};

  const entries = filteredRecords.flatMap((record) => {
    const recordDay = resolveDayNumber(record);
    const feedbackEntries = Array.isArray(record.feedback) ? record.feedback : [];
    return feedbackEntries.filter((entry) => {
      if (!showDayTabs || resolvedDayNumber == null) return true;
      const entryDay = resolveDayNumber(entry);
      const effectiveDay = entryDay ?? recordDay;
      return effectiveDay === resolvedDayNumber;
    });
  });
  const recordsForAverage = useMemo(() => {
    const base = filteredRecords.filter((record) => !isAbandonedMatchFeedback(record));
    if (!showDayTabs || resolvedDayNumber == null) return base;
    return base.filter((record) => resolveDayNumber(record) === resolvedDayNumber);
  }, [filteredRecords, showDayTabs, resolvedDayNumber]);

  const averageTotal =
    recordsForAverage.length > 0
      ? (
          recordsForAverage.reduce((sum, record) => sum + Number(record.averageTotal || 0), 0) /
          recordsForAverage.length
        ).toFixed(1)
      : null;
  // Collect all unique parameter names across ALL entries
  const paramNames = Array.from(
    new Set(
      entries.flatMap((entry) =>
        Array.isArray(entry?.scores)
          ? entry.scores.filter(Boolean).map((s) => s.parameterName).filter(Boolean)
          : []
      )
    )
  );

  // Helper: get score value for a param from an entry
  const getScore = (entry, paramName) => {
    if (!Array.isArray(entry.scores)) return null;
    const found = entry.scores.filter(Boolean).find((s) => s.parameterName === paramName);
    return found ? found.score : null;
  };

  const primaryRecordForDay = useMemo(() => {
    const fallback = scopedRecords[0] || validRecords[0] || {};
    if (showDayTabs && resolvedDayNumber != null) {
      return (
        filteredRecords.find((r) => resolveDayNumber(r) === resolvedDayNumber) || fallback
      );
    }
    return fallback;
  }, [filteredRecords, scopedRecords, validRecords, showDayTabs, resolvedDayNumber]);

  const evaluatorName =
    primaryRecordForDay?.evaluator?.name ||
    primaryRecordForDay?.evaluator?.fullName ||
    primaryRecordForDay?.createdBy?.name ||
    primaryRecordForDay?.createdBy?.fullName ||
    baseRecord?.evaluator?.name ||
    baseRecord?.evaluator?.fullName ||
    '';

  const overallFeedbackText = String(primaryRecordForDay?.overallFeedback ?? '').trim();

  const isViewAbandoned = useMemo(
    () => isAbandonedMatchFeedback(primaryRecordForDay),
    [primaryRecordForDay]
  );

  const getEntryRowAverage = (entry) => {
    const t = Number(entry?.total);
    if (Number.isFinite(t)) return t;
    if (!Array.isArray(entry?.scores)) return null;
    const nums = entry.scores
      .map((s) => s?.score)
      .filter((v) => {
        const n = Number(v);
        return Number.isFinite(n);
      })
      .map(Number);
    if (!nums.length) return null;
    return nums.reduce((a, b) => a + b, 0) / nums.length;
  };

  const quickSummary = useMemo(() => {
    if (!entries.length) return 0;
    const vals = entries.map(getEntryRowAverage).filter((v) => v != null && Number.isFinite(v));
    if (!vals.length) return 0;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [entries]);

  if (loading) {
    return (
      <div style={{ padding: '20px', background: '#eef2f6', minHeight: '100%' }}>
        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(15, 23, 42, 0.07)',
            border: '1px solid #e5e7eb',
            padding: '40px',
            textAlign: 'center',
            color: '#98a2b3',
            fontSize: '13px',
          }}
        >
          Loading...
        </div>
      </div>
    );
  }

  if (!feedbackData) return null;

  const teamNameDisplay = team?.teamName || team?.name || '—';

  return (
    <div style={{ padding: '20px', background: '#eef2f6', minHeight: '100%' }}>
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(15, 23, 42, 0.07)',
          border: '1px solid #e5e7eb',
          padding: '22px 24px 26px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '16px',
            marginBottom: '22px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', minWidth: 0 }}>
            <div
              aria-hidden
              style={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: 'rgba(102, 126, 234, 0.16)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: '#667eea',
              }}
            >
              <FiBarChart2 size={24} strokeWidth={2} />
            </div>
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#1e1b4b',
                  letterSpacing: '-0.02em',
                }}
              >
                Match Feedback
              </h1>
              <p
                style={{
                  margin: '6px 0 0',
                  fontSize: '13px',
                  color: '#64748b',
                  lineHeight: 1.45,
                  maxWidth: '540px',
                }}
              >
                Team lead performance scores by role and member.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              const fromPath = String(location.state?.from || '').trim();
              navigate(fromPath || `${appBasePath}/matches`);
            }}
            style={{
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '34px',
              padding: '0 14px',
              border: '1px solid #d0d5dd',
              background: '#ffffff',
              color: '#1e293b',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            <FiArrowLeft size={14} strokeWidth={2.2} aria-hidden />
            Back
          </button>
        </div>

        <div style={FEEDBACK_SUMMARY_CARD_OUTER} role="region" aria-label="Match summary">
          <div style={SUMMARY_DETAILS_GRID}>
            <div style={SUMMARY_DETAIL_ITEM}>
              <div>
                <p style={SUMMARY_DETAIL_LABEL}>Match Name</p>
                <p style={SUMMARY_DETAIL_VALUE_SMALL}>{match.matchName || '—'}</p>
              </div>
              <div style={{ marginTop: '14px' }}>
                <p style={SUMMARY_DETAIL_LABEL}>Average Rating</p>
                <p style={SUMMARY_DETAIL_VALUE_SMALL}>
                  {isViewAbandoned ? 'NA' : averageTotal != null ? `${averageTotal} / 5.0` : '—'}
                </p>
              </div>
            </div>
            <div style={SUMMARY_DETAIL_ITEM}>
              <div>
                <p style={SUMMARY_DETAIL_LABEL}>Venue</p>
                <p style={SUMMARY_DETAIL_VALUE_SMALL}>{match.matchLocation || '—'}</p>
              </div>
              <div style={{ marginTop: '14px' }}>
                <p style={SUMMARY_DETAIL_LABEL}>Evaluator</p>
                <p style={SUMMARY_DETAIL_VALUE_SMALL}>{evaluatorName || '—'}</p>
              </div>
            </div>
            <div style={SUMMARY_DETAIL_ITEM}>
              <p style={SUMMARY_DETAIL_LABEL}>Team</p>
              <p style={SUMMARY_DETAIL_VALUE_SMALL}>{teamNameDisplay}</p>
            </div>
            <div style={SUMMARY_DETAIL_ITEM}>
              <p style={SUMMARY_DETAIL_LABEL}>Start Date & Time</p>
              <p style={SUMMARY_DETAIL_VALUE_SMALL}>{formatDateTime(match.startDate, match.startTime)}</p>
            </div>
            <div style={SUMMARY_DETAIL_ITEM}>
              <p style={SUMMARY_DETAIL_LABEL}>End Date & Time</p>
              <p style={SUMMARY_DETAIL_VALUE_SMALL}>{formatDateTime(match.endDate, match.endTime)}</p>
            </div>
          </div>
        </div>

        {/* Feedback Table */}
        {showDayTabs && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: '2px', marginBottom: '10px' }}>
            {dayOptions.map((day) => {
              const isSelected = resolvedDayNumber === day;
              const pillStyle = getDayPillStyle(isSelected);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedDay(String(day))}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '999px',
                    ...pillStyle,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatMatchDayPillLabel(day, match.startDate || match.date)}
                </button>
              );
            })}
          </div>
        )}
        {entries.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#98a2b3', fontSize: '13px', padding: '32px 0' }}>No feedback entries found.</div>
        ) : (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: `${320 + paramNames.length * 110}px` }}>
                <thead style={FEEDBACK_TABLE_HEAD_GRADIENT}>
                  <tr>
                    <th
                      style={{
                        padding: '12px 14px',
                        textAlign: 'center',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        width: '52px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      S.No
                    </th>
                    <th
                      style={{
                        padding: '12px 14px',
                        textAlign: 'left',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Job Type
                    </th>
                    <th
                      style={{
                        padding: '12px 14px',
                        textAlign: 'left',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Member
                    </th>
                    {paramNames.map((p) => (
                      <th
                        key={p}
                        style={{
                          padding: '12px 14px',
                          textAlign: 'center',
                          fontSize: '12px',
                          color: '#f8fafc',
                          fontWeight: 700,
                          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {p}
                      </th>
                    ))}
                    <th
                      style={{
                        padding: '12px 14px',
                        textAlign: 'center',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Average
                    </th>
                    <th
                      style={{
                        padding: '12px 14px',
                        textAlign: 'left',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Remarks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, idx) => {
                    const totalNumber = Number(entry.total);
                    const displayTotal = isViewAbandoned
                      ? 'NA'
                      : Number.isFinite(totalNumber)
                        ? formatSingleDecimal(entry.total)
                        : formatScoreBadgeText(entry.total ?? '-');
                    const totalBadge = averageScoreBadge(
                      isViewAbandoned ? null : Number.isFinite(totalNumber) ? totalNumber : entry.total
                    );
                    const empKey = entry.employeeId?._id ?? entry.employeeId?.id ?? idx;
                    const RoleIcon = getRoleIconComponent(entry.role);
                    return (
                      <tr key={`${entry.role || ''}-${empKey}-${idx}`} style={{ borderTop: '1px solid #e5e7eb', background: '#ffffff' }}>
                        <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: '13px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {idx + 1}
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#334155', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }}>
                            <RoleIcon size={17} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                            {entry.role || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#334155', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }}>
                            <FiUser size={17} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                            {entry.employeeId?.name || entry.employeeId?.fullName || '-'}
                          </span>
                        </td>
                        {paramNames.map((p) => {
                          const val = getScore(entry, p);
                          const { bg, color } = metricScoreBadge(val);
                          return (
                            <td key={p} style={{ padding: '12px 14px', textAlign: 'center', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  minWidth: '32px',
                                  padding: '4px 10px',
                                  borderRadius: '8px',
                                  background: bg,
                                  color,
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                {formatScoreBadgeText(val ?? '-')}
                              </span>
                            </td>
                          );
                        })}
                        <td style={{ padding: '12px 14px', textAlign: 'center', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              minWidth: '36px',
                              padding: '4px 10px',
                              borderRadius: '8px',
                              background: totalBadge.bg,
                              color: totalBadge.color,
                              fontSize: '12px',
                              fontWeight: 700,
                            }}
                          >
                            {displayTotal}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '12px', color: '#667085', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {formatScoreOrRemarkForDisplay(entry.remarks)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Overall Feedback + Quick Summary (aligned with Event Feedback form) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.9fr', gap: '12px', marginTop: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: '#667085', fontWeight: 700, marginBottom: '6px' }}>
              OVERALL FEEDBACK
            </label>
            <div
              role="region"
              aria-label="Overall feedback"
              tabIndex={-1}
              style={{
                minHeight: '96px',
                width: '100%',
                padding: '10px',
                boxSizing: 'border-box',
                border: '1px solid #eaecf0',
                borderRadius: '12px',
                fontSize: '13px',
                fontFamily: 'inherit',
                lineHeight: 1.6,
                background: '#f8fafc',
                color: overallFeedbackText ? '#344054' : '#98a2b3',
                cursor: 'default',
                pointerEvents: overallFeedbackText ? 'auto' : 'none',
                userSelect: overallFeedbackText ? 'text' : 'none',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {overallFeedbackText || 'No feedback'}
            </div>
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ border: '1px dashed #d0d5dd', borderRadius: '12px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: '#667085', fontWeight: 700 }}>QUICK SUMMARY</div>
              <div style={{ marginTop: '4px', fontSize: '38px', lineHeight: 1, fontWeight: 700, color: '#101828' }}>
                {isViewAbandoned ? 'N/A' : entries.length === 0 ? '—' : quickSummary.toFixed(1)}
              </div>
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#667085' }}>Average</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchFeedbackViewPage;
